import * as React from 'react';
import { Button, Dropdown, Input, MessageBar, MessageBarBody, Option, Spinner } from '@fluentui/react-components';
import { CheckmarkRegular, CopyRegular } from '@fluentui/react-icons';
import { rulesFor, type EventKind } from '../../api/src/programs';
import { errorText, useApp } from '../app/context';
import { getItem, getItemByPath, startCopy } from '../graphx';
import { ensureFolders } from '../records/folders';
import type { IEventInput, IEventView } from '../records/events';
import { DeadlinePreview, EventDates, eventInputReady } from '../components/EventEntry';
import { localToday, outstandingCount, stageLabel } from '../records/model';
import { buildPackage, type IPackageFile } from '../records/package';
import { recordEvent } from '../records/recordEvent';
import { addInstalments } from '../records/stage';
import type { ITabData } from '../records/requests';
import { useLoad } from '../records/useLoad';

const PORTALS: string[] = ['Authorized Paid Representatives Portal', 'Permanent Residence Portal', 'IRCC Portal', 'IRCC secure account', 'Employer Portal', 'IRB My Case', 'Federal Court e-filing', 'Paper (by mail or courier)', 'Other'];
const SIGNOFFS: { title: string; role: 'clerk' | 'lawyer' }[] = [{ title: 'Clerk sign-off', role: 'clerk' }, { title: 'Lawyer sign-off', role: 'lawyer' }];

/** Signing off, the package for IRCC, and recording the submission. */
export function SubmissionTab(props: { data: ITabData; onChanged: () => void }): React.ReactElement {
  const app = useApp();
  const d: ITabData = props.data;
  const a = d.application;
  const today: string = localToday();
  const left: number = outstandingCount(d.items);
  const signoffs: IEventView[] = d.events.filter(e => e.kind === 'signoff' && e.doneOn);
  const signed = (title: string): IEventView | undefined => signoffs.filter(e => e.title === title)[0];
  const lawyers = (app.workspace.firm?.staff ?? []).filter(s => s.role === 'lawyer');
  const me: string = app.account.username.toLowerCase();
  const isLawyer: boolean = !lawyers.some(l => l.email) || lawyers.some(l => (l.email ?? '').toLowerCase() === me);
  const [working, setWorking] = React.useState<string | undefined>(undefined);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [done, setDone] = React.useState<string | undefined>(undefined);
  const [on, setOn] = React.useState<string>(today);
  const [via, setVia] = React.useState<string>('');
  const [confirmation, setConfirmation] = React.useState<string>('');

  const docIds: string[] = d.items.reduce<string[]>((ids, i) => ids.concat(i.documents.map(x => x.id)), []);
  const { data: sizes } = useLoad(async () => {
    const out: { [id: string]: number | undefined } = {};
    for (const id of docIds) {
      out[id] = (await getItem(app.graph, d.set.library.driveId, id).catch(() => undefined))?.size;
    }
    return out;
  }, [docIds.join('|')]);
  const pkg: IPackageFile[] = buildPackage(d.items, d.people, sizes ?? {}, d.program?.sections ?? []);

  const run = async (what: string, step: () => Promise<void>): Promise<void> => {
    setWorking(what);
    setError(undefined);
    setDone(undefined);
    try {
      await step();
      props.onChanged();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setWorking(undefined);
    }
  };

  const signOff = (title: string): Promise<void> => run('Signing off…', async () => {
    await d.set.writer.batch().create(d.set.events, {
      Title: title, ApplicationKey: a.key, Kind: 'signoff', DoneBy: app.account.username, DoneOn: new Date().toISOString(),
      Note: left ? `${left} item${left === 1 ? '' : 's'} still outstanding when signed off.` : undefined
    }, title, `${title} on ${a.title} by ${app.account.name || app.account.username}`);
  });

  const copyPackage = (): Promise<void> => run('Copying the package…', async () => {
    const library = { ...app.workspace, driveId: d.set.library.driveId, libraryName: d.set.library.libraryName };
    const folder: string = `${await folderPathOf(a.folderId)}/Submission package ${today}`;
    await ensureFolders(app.manageGraph, library, d.set.audit, app.account.username, [folder]);
    const target = await getItemByPath(app.graph, d.set.library.driveId, folder);
    if (!target) {
      throw new Error('The package folder couldn\'t be created.');
    }
    for (const f of pkg) {
      await startCopy(app.graph, d.set.library.driveId, f.sourceId, target.id, f.name);
    }
    setDone(`Copying ${pkg.length} file${pkg.length === 1 ? '' : 's'} into "Submission package ${today}" in the application's folder. The originals stay where they are.`);
  });

  // The folder's path in the library, from its id (the package folder goes inside it).
  const folderPathOf = async (id: string | undefined): Promise<string> => {
    const item: { parentReference?: { path?: string }; name?: string } = await app.graph.api(`/drives/${d.set.library.driveId}/items/${id}?$select=name,parentReference`).get();
    const parent: string = (item.parentReference?.path ?? '').replace(/^\/drives\/[^/]+\/root:?/, '').replace(/^\//, '');
    return decodeURIComponent(parent ? `${parent}/${item.name}` : String(item.name));
  };

  const record = (): Promise<void> => run('Recording the submission…', async () => {
    const batch = d.set.writer.batch();
    await batch.update(d.set.applications, a.rec, {
      SubmittedOn: on, SubmittedVia: via, Confirmation: confirmation.trim() || undefined, Stage: stageLabel('submitted')
    }, a.title, `Recorded ${a.title} as submitted on ${on} through ${via}`);
    await addInstalments(d.set, batch, a, 'submitted', app.workspace.firm?.instalments, d.events);
  });

  const bothSigned: boolean = SIGNOFFS.every(s => !!signed(s.title));
  return (
    <>
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {done && <MessageBar intent="success"><MessageBarBody>{done}</MessageBarBody></MessageBar>}
      <section className="card" style={{ gap: 10 }}>
        <h2>Sign-offs</h2>
        {left > 0 && <MessageBar intent="warning"><MessageBarBody>{left} checklist item{left === 1 ? ' is' : 's are'} still outstanding.</MessageBarBody></MessageBar>}
        {SIGNOFFS.map(s => {
          const e: IEventView | undefined = signed(s.title);
          return (
            <div key={s.title} className="item-row">
              <div><strong>{s.title}</strong><div className="small">{e ? `Signed off by ${e.doneBy}, ${new Date(e.doneOn!).toLocaleString()}${e.note ? `. ${e.note}` : ''}` : 'Not yet'}</div></div>
              <div>{!e && <Button disabled={!!working || (s.role === 'lawyer' && !isLawyer)} onClick={() => { void signOff(s.title); }}>Sign off</Button>}</div>
            </div>
          );
        })}
        {!isLawyer && (
          <MessageBar intent="warning"><MessageBarBody>
            You're signed in as {app.account.username}, which isn't the email of a lawyer on your firm's list, so the lawyer sign-off is switched off.
            {lawyers.some(l => l.email)
              ? ` Lawyers with an email on the list: ${lawyers.filter(l => l.email).map(l => `${l.name} (${l.email})`).join(', ')}. Sign in as one of them, or `
              : ' '}
            {lawyers.length ? 'put this email on the lawyer\'s entry in ' : 'add the lawyer, with their email, in '}<a href="#/settings">Settings</a>.
          </MessageBarBody></MessageBar>
        )}
      </section>

      <section className="card" style={{ gap: 10 }}>
        <h2>Package for IRCC</h2>
        <span className="small" style={{ fontSize: 14 }}>The documents attached to received items, named for upload, in checklist order. Copies are made in a new folder; the originals don't move.</span>
        {!sizes && docIds.length > 0 && <Spinner size="tiny" label="Checking file sizes…" labelPosition="after" />}
        {pkg.length === 0 && <span className="small" style={{ fontSize: 15 }}>No received items with documents attached yet.</span>}
        {pkg.length > 0 && (
          <table className="data-table">
            <thead><tr><th>Name in the package</th><th>From</th><th>Size</th></tr></thead>
            <tbody>{pkg.map(f => (
              <tr key={`${f.sourceId}|${f.name}`}>
                <td>{f.name}{f.warnings.map(w => <div key={w} className="small" style={{ color: 'var(--red)' }}>{w}</div>)}</td>
                <td className="small">{f.sourceName}</td>
                <td className="small">{f.size !== undefined ? `${(f.size / 1024 / 1024).toFixed(1)} MB` : '—'}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
        <Button icon={<CopyRegular />} disabled={!!working || !pkg.length || !a.folderId} onClick={() => { void copyPackage(); }} style={{ alignSelf: 'flex-start' }}>Copy into a package folder</Button>
      </section>

      <section className="card" style={{ gap: 10 }}>
        <h2>Record the submission</h2>
        {a.submittedOn
          ? <span style={{ fontSize: 15 }}>Submitted on {a.submittedOn} through {a.submittedVia}{a.confirmation ? ` · confirmation ${a.confirmation}` : ''}.</span>
          : (
            <>
              <div className="form-grid">
                <label>Submitted on<Input type="date" max={today} value={on} onChange={(_, x) => setOn(x.value)} /></label>
                <label>Through
                  <Dropdown placeholder="Choose" value={via} selectedOptions={via ? [via] : []} onOptionSelect={(_, x) => setVia(x.optionValue ?? '')}>
                    {PORTALS.map(p => <Option key={p} value={p}>{p}</Option>)}
                  </Dropdown>
                </label>
                <label>Confirmation or application number<Input value={confirmation} onChange={(_, x) => setConfirmation(x.value)} /></label>
              </div>
              {!bothSigned && <span className="small">Both sign-offs are needed first.</span>}
              <Button appearance="primary" disabled={!!working || !bothSigned || !on || !via} onClick={() => { void record(); }} style={{ alignSelf: 'flex-start' }}>Record as submitted</Button>
            </>
          )}
      </section>
      {working && <span className="small">{working}</span>}
    </>
  );
}

// Worded as IRCC's decision: the app never says a file is approved, only what IRCC decided.
const OUTCOMES: string[] = ['Approved by IRCC', 'Refused by IRCC', 'Withdrawn', 'Returned as incomplete'];
const isApproved = (outcome: string): boolean => outcome === 'Approved by IRCC' || outcome === 'Approved';
const isRefused = (outcome: string): boolean => outcome === 'Refused by IRCC' || outcome === 'Refused';
/** Kinds of refusal, offered when the program has a rule for them; the shorter period comes first. */
const REFUSALS: { kind: EventKind; label: string }[] = [
  { kind: 'refusalInCanada', label: 'Leave to the Federal Court (15 days; 60 only if the matter arose outside Canada)' },
  { kind: 'sponsorshipRefusal', label: 'Sponsorship refusal (Immigration Appeal Division: 30 days)' },
  { kind: 'residencyRemovalOrder', label: 'Removal order made in Canada, port of entry or inland (Immigration Appeal Division: 30 days)' },
  { kind: 'residencyRefusal', label: 'Residency decision made outside Canada by an officer abroad (Immigration Appeal Division: 60 days)' }
];
export const CLOSING_TASKS: string[] = [
  'Final account sent (and any trust balance settled in your accounting)',
  'Original documents returned to the client',
  'Closing letter sent to the client',
  'Follow-up dates set below'
];
const FOLLOW_UPS: string[] = ['Permit or visa expires: renewal', 'PR card renewal', 'Citizenship: check eligibility', 'Other'];

/** The decision, closing steps, follow-up dates, and closing the file. */
export function ClosingTab(props: { data: ITabData; onChanged: () => void }): React.ReactElement {
  const app = useApp();
  const d: ITabData = props.data;
  const a = d.application;
  const today: string = localToday();
  const [outcome, setOutcome] = React.useState<string>('');
  const [input, setInput] = React.useState<IEventInput>({ letterDate: today });
  const refusals = REFUSALS.filter(r => d.program && rulesFor(d.program, r.kind).length > 0);
  // Residency files default to the 30-day removal order appeal, the shorter of the two.
  const [refusal, setRefusal] = React.useState<EventKind | ''>(refusals.some(r => r.kind === 'residencyRemovalOrder') ? 'residencyRemovalOrder' : '');
  const [fuLabel, setFuLabel] = React.useState<string>('');
  const [fuOther, setFuOther] = React.useState<string>('');
  const [fuDate, setFuDate] = React.useState<string>('');
  const [working, setWorking] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const tasks: IEventView[] = d.events.filter(e => e.kind === 'task');
  const followUps: IEventView[] = d.events.filter(e => e.kind === 'followup').sort((x, y) => (x.due ?? '').localeCompare(y.due ?? ''));

  const run = async (step: () => Promise<void>): Promise<void> => {
    setWorking(true);
    setError(undefined);
    try {
      await step();
      props.onChanged();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setWorking(false);
    }
  };

  const recordDecision = (): Promise<void> => run(async () => {
    const batch = d.set.writer.batch();
    const date: string = input.letterDate || input.receivedDate || today;
    await batch.update(d.set.applications, a.rec, { Outcome: outcome, DecisionDate: date, Stage: stageLabel('decision') }, a.title, `Recorded the decision on ${a.title}: ${outcome} (${date})`);
    if (d.program && (isApproved(outcome) || (isRefused(outcome) && refusal))) {
      await recordEvent(d.set, batch, a, d.program, isApproved(outcome) ? 'decisionApproved' : refusal as EventKind, input, undefined);
    }
    await addInstalments(d.set, batch, a, 'decision', app.workspace.firm?.instalments, d.events);
  });

  const startClosing = (): Promise<void> => run(async () => {
    const batch = d.set.writer.batch();
    await batch.update(d.set.applications, a.rec, { Stage: stageLabel('closing') }, a.title, `Started closing ${a.title}`);
    for (const t of CLOSING_TASKS.filter(t => !tasks.some(x => x.title === t))) {
      await batch.create(d.set.events, { Title: t, ApplicationKey: a.key, Kind: 'task' }, t, `Closing step "${t}" added to ${a.title}`);
    }
    await addInstalments(d.set, batch, a, 'closing', app.workspace.firm?.instalments, d.events);
  });

  const taskDone = (t: IEventView): Promise<void> => run(async () => {
    await d.set.writer.batch().update(d.set.events, t.rec, { DoneBy: app.account.username, DoneOn: new Date().toISOString() }, t.title, `Done: ${t.title} (${a.title})`);
  });

  const addFollowUp = (): Promise<void> => run(async () => {
    const label: string = fuLabel === 'Other' ? fuOther.trim() : fuLabel;
    await d.set.writer.batch().create(d.set.events, { Title: label, ApplicationKey: a.key, Kind: 'followup', Due: fuDate }, label, `Follow-up "${label}" on ${fuDate} for ${a.title}`);
    setFuLabel('');
    setFuOther('');
    setFuDate('');
  });

  const close = (): Promise<void> => run(async () => {
    const batch = d.set.writer.batch();
    await batch.update(d.set.applications, a.rec, { Stage: stageLabel('closed'), Open: false, ClosedOn: today }, a.title, `Closed ${a.title}`);
    await addInstalments(d.set, batch, a, 'closed', app.workspace.firm?.instalments, d.events);
  });

  const tasksLeft: number = tasks.filter(t => !t.doneOn).length;
  return (
    <>
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      <section className="card" style={{ gap: 10 }}>
        <h2>Decision</h2>
        {a.outcome
          ? <span style={{ fontSize: 15 }}>{a.outcome}, {a.decisionDate}. {isRefused(a.outcome) ? 'Deadlines that follow are on the IRCC & deadlines tab.' : ''}</span>
          : (
            <>
              <div className="form-grid">
                <label>Outcome
                  <Dropdown placeholder="Choose" value={outcome} selectedOptions={outcome ? [outcome] : []} onOptionSelect={(_, x) => setOutcome(x.optionValue ?? '')}>
                    {OUTCOMES.map(o => <Option key={o} value={o}>{o}</Option>)}
                  </Dropdown>
                </label>
                {isRefused(outcome) && (
                  <label>Kind of refusal
                    <Dropdown placeholder="Choose" value={refusals.filter(r => r.kind === refusal)[0]?.label ?? ''} selectedOptions={refusal ? [refusal] : []} onOptionSelect={(_, x) => setRefusal((x.optionValue ?? '') as EventKind)}>
                      {refusals.map(r => <Option key={r.kind} value={r.kind}>{r.label}</Option>)}
                    </Dropdown>
                  </label>
                )}
                <EventDates kind={isRefused(outcome) && refusal ? refusal : 'decisionApproved'} input={input} onChange={setInput} letterLabel="Date on the decision letter" />
              </div>
              {isRefused(outcome) && refusal && d.program && <DeadlinePreview program={d.program} kind={refusal} input={input} onChange={setInput} />}
              {isRefused(outcome) && <span className="small">The deadline to challenge the refusal is added as calculated, for you to check and confirm. Counting usually starts the day the decision was received.</span>}
              <Button appearance="primary" disabled={working || !outcome || !eventInputReady(isRefused(outcome) && refusal ? refusal : 'decisionApproved', input, today) || (isRefused(outcome) && !refusal)} onClick={() => { void recordDecision(); }} style={{ alignSelf: 'flex-start' }}>Record the decision</Button>
            </>
          )}
      </section>

      <section className="card" style={{ gap: 6 }}>
        <h2>Closing</h2>
        {tasks.length === 0
          ? <Button disabled={working} onClick={() => { void startClosing(); }} style={{ alignSelf: 'flex-start' }}>Start closing the file</Button>
          : tasks.map(t => (
            <div key={t.key} className="item-row">
              <div><span style={{ fontWeight: 600 }}>{t.title}</span>{t.doneOn && <div className="small">Done by {t.doneBy}, {new Date(t.doneOn).toLocaleDateString()}</div>}</div>
              <div>{!t.doneOn && <Button size="small" icon={<CheckmarkRegular />} disabled={working} onClick={() => { void taskDone(t); }}>Done</Button>}</div>
            </div>
          ))}
      </section>

      <section className="card" style={{ gap: 10 }}>
        <h2>Follow-up dates</h2>
        <span className="small" style={{ fontSize: 14 }}>Dates to come back to the client, such as a permit renewal. They show on Today even after the file is closed.</span>
        {followUps.map(f => (
          <div key={f.key} className="item-row">
            <span style={{ fontSize: 15 }}>{f.due}: {f.title}{f.doneOn ? <span className="small"> · done by {f.doneBy}</span> : null}</span>
            <div>{!f.doneOn && <Button size="small" icon={<CheckmarkRegular />} disabled={working} onClick={() => { void taskDone(f); }}>Done</Button>}</div>
          </div>
        ))}
        <div className="form-grid">
          <label>What
            <Dropdown placeholder="Choose" value={fuLabel} selectedOptions={fuLabel ? [fuLabel] : []} onOptionSelect={(_, x) => setFuLabel(x.optionValue ?? '')}>
              {FOLLOW_UPS.map(o => <Option key={o} value={o}>{o}</Option>)}
            </Dropdown>
          </label>
          {fuLabel === 'Other' && <label>Describe it<Input value={fuOther} onChange={(_, x) => setFuOther(x.value)} /></label>}
          <label>On<Input type="date" min={today} value={fuDate} onChange={(_, x) => setFuDate(x.value)} /></label>
        </div>
        <Button disabled={working || !fuLabel || !fuDate || (fuLabel === 'Other' && !fuOther.trim())} onClick={() => { void addFollowUp(); }} style={{ alignSelf: 'flex-start' }}>Add the follow-up</Button>
      </section>

      <section className="card" style={{ gap: 10 }}>
        <h2>Close the file</h2>
        {a.closedOn
          ? <span style={{ fontSize: 15 }}>Closed on {a.closedOn}.</span>
          : (
            <>
              {tasksLeft > 0 && <span className="small">{tasksLeft} closing step{tasksLeft === 1 ? '' : 's'} not done yet.</span>}
              <span className="small" style={{ fontSize: 14 }}>The file leaves the board; its records, documents and follow-up dates stay. You can reopen it by changing its stage.</span>
              <Button disabled={working} onClick={() => { void close(); }} style={{ alignSelf: 'flex-start' }}>Close the file</Button>
            </>
          )}
      </section>
    </>
  );
}
